import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, Calendar, Receipt, Target, FileText, Calculator, BarChart3, Shield, Zap, MessageCircle, Sparkles } from "lucide-react";

const MODULES = [
  { icon: Calendar, title: "Motor de reservas", desc: "Calendário visual, regras por plano, aprovação automática, check-in com QR Code." },
  { icon: Target, title: "CRM completo", desc: "Pipeline kanban, captura de leads, SLA visual, conversão por origem." },
  { icon: Receipt, title: "Financeiro", desc: "Cobranças recorrentes PIX/boleto/cartão via Asaas. Inadimplência automatizada." },
  { icon: FileText, title: "Contratos digitais", desc: "Modelos personalizados, assinatura eletrônica e renovação automática." },
  { icon: Calculator, title: "Motor de precificação", desc: "Custos, ponto de equilíbrio, simulador, sugestão automática de preços." },
  { icon: BarChart3, title: "Relatórios premium", desc: "MRR, churn, ocupação, ticket médio, exportação para PDF e Excel." },
  { icon: Building2, title: "Multiunidades", desc: "Suporte a múltiplas unidades com consolidação de resultados." },
  { icon: MessageCircle, title: "WhatsApp integrado", desc: "Confirmações, lembretes e cobrança automática via Z-API, Twilio ou Evolution." },
];

const FEATURES = [
  "Site institucional white-label",
  "Portal do cliente premium",
  "Painel administrativo completo",
  "Logs de auditoria",
  "RBAC com 6 perfis",
  "Backup automatizado",
  "Health check + monitoramento",
  "Rate limiting",
  "Sentry-ready",
  "Docker + docker-compose",
];

export default function SaaS() {
  return (
    <div className="theme-marketing min-h-screen" data-testid="saas-landing">
      <div className="bg-[#0A1128]">
        {/* Nav */}
        <header className="absolute top-0 left-0 right-0 z-10 px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-[#D4AF37]/40 flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-lg">VM</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-white text-base">VM Corporate</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.2em]">Software</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/70 hover:text-white">Site do coworking</Link>
            <Link to="/signup" className="btn-gold px-5 py-2 rounded-md text-sm" data-testid="saas-nav-signup">Trial grátis</Link>
          </div>
        </header>

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-12 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#14213D]/60 to-[#0A1128] pointer-events-none" />
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D4AF37]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 mb-8">
                <Sparkles size={14} className="text-[#D4AF37]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Para coworkings · SaaS B2B</span>
              </div>
              <h1 className="font-display text-5xl lg:text-7xl text-white font-light leading-[1.05]">
                A plataforma que
                <span className="block italic text-[#D4AF37] mt-2">eleva seu coworking.</span>
              </h1>
              <p className="text-lg text-white/70 mt-8 max-w-2xl leading-relaxed">
                Gestão operacional, comercial e financeira em um único sistema. Reservas, contratos, cobranças, CRM, BI e WhatsApp.
                Construído para coworkings premium que crescem rápido.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/signup" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm" data-testid="saas-cta-trial">
                  Começar trial 14 dias grátis <ArrowRight size={16} />
                </Link>
                <a href="#pricing" className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm">
                  Ver planos
                </a>
              </div>
              <div className="mt-16 grid grid-cols-3 gap-8 max-w-xl">
                {[{ n: "8+", l: "Módulos" }, { n: "100%", l: "Em PT-BR" }, { n: "SaaS", l: "Multi-tenant" }].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-3xl text-[#D4AF37] font-light">{s.n}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-marketing p-7 tracing-line backdrop-blur-md bg-[#0A1128]/80">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-2">Tudo já incluso</div>
                <div className="font-display text-2xl text-white mb-5">Recursos da plataforma</div>
                <ul className="space-y-2.5">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                      <Check size={14} className="text-[#D4AF37] flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Modules */}
        <section id="modulos" className="px-6 lg:px-12 py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Módulos</div>
              <h2 className="font-display text-4xl lg:text-5xl text-white font-light">
                Tudo que seu coworking precisa,
                <span className="block italic text-[#D4AF37]">em um único lugar.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {MODULES.map((m) => (
                <div key={m.title} className="card-marketing p-6 h-full">
                  <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                    <m.icon size={18} className="text-[#D4AF37]" />
                  </div>
                  <div className="font-display text-white text-lg mb-1">{m.title}</div>
                  <div className="text-xs text-white/60 leading-relaxed">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentials */}
        <section className="px-6 lg:px-12 py-24 border-t border-white/5">
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Pronto para produção", text: "Backup automatizado, rate limiting, logs de auditoria, Sentry-ready, Docker." },
              { icon: Zap, title: "Integrações nativas", text: "Asaas, WhatsApp (Z-API/Twilio/Evolution), assinatura eletrônica in-app." },
              { icon: Building2, title: "Escalável", text: "Arquitetura multi-tenant pronta. Adicione unidades sem reescrever código." },
            ].map((d) => (
              <div key={d.title} className="card-marketing p-7 text-center">
                <div className="w-12 h-12 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
                  <d.icon size={20} className="text-[#D4AF37]" />
                </div>
                <div className="font-display text-white text-xl mb-2">{d.title}</div>
                <div className="text-sm text-white/60">{d.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section id="pricing" className="px-6 lg:px-12 py-24 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Planos</div>
              <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight">
                Preço justo,
                <span className="italic text-[#D4AF37]"> sem surpresas.</span>
              </h2>
              <p className="text-white/60 mt-4">Comece grátis por 14 dias. Sem cartão. Sem fidelidade.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: "Starter", price: "R$ 297", per: "/mês", desc: "Para coworkings começando", feats: ["1 unidade", "Até 30 clientes", "Reservas + CRM básico", "Financeiro", "Suporte por e-mail"] },
                { name: "Professional", price: "R$ 597", per: "/mês", desc: "Para coworkings em crescimento", feats: ["Até 3 unidades", "Até 200 clientes", "Todos os módulos", "Asaas + WhatsApp", "Exportação PDF/Excel", "Suporte prioritário"], highlight: true },
                { name: "Enterprise", price: "Sob consulta", per: "", desc: "Para grandes operações", feats: ["Unidades ilimitadas", "Clientes ilimitados", "White-label", "API dedicada", "Account manager"] },
              ].map((p) => (
                <div key={p.name} className={`card-marketing p-7 flex flex-col ${p.highlight ? "border-[#D4AF37]/60 bg-[#D4AF37]/5 tracing-line" : ""}`}>
                  {p.highlight && (
                    <div className="inline-flex items-center gap-1 self-start mb-3 px-3 py-1 bg-[#D4AF37] text-[#0A1128] text-[10px] uppercase tracking-[0.18em] font-bold rounded-full">
                      Mais escolhido
                    </div>
                  )}
                  <div className="font-display text-2xl text-white">{p.name}</div>
                  <div className="text-xs text-white/60 mt-1 mb-5">{p.desc}</div>
                  <div className="mb-6">
                    <span className="font-display text-4xl text-[#D4AF37]">{p.price}</span>
                    <span className="text-sm text-white/50 ml-1">{p.per}</span>
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {p.feats.map((f, i) => (
                      <li key={i} className="flex gap-2 text-sm text-white/80"><Check size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <Link to="/signup" className={`block text-center px-5 py-2.5 rounded-md text-sm ${p.highlight ? "btn-gold" : "btn-ghost-gold"}`}>
                    {p.name === "Enterprise" ? "Falar com vendas" : "Começar grátis"}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-6 lg:px-12 py-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">FAQ</div>
              <h2 className="font-display text-4xl text-white font-light">Perguntas frequentes</h2>
            </div>
            <div className="space-y-3">
              {[
                { q: "Como funciona o trial de 14 dias?", a: "Você cria sua conta em menos de 1 minuto e ganha 14 dias completos para testar todos os módulos premium, sem precisar de cartão de crédito. Ao final, basta escolher um plano para continuar." },
                { q: "Preciso de cartão para começar?", a: "Não. Você começa o trial só com e-mail e senha." },
                { q: "Posso cancelar quando quiser?", a: "Sim. Não há fidelidade. Cancele direto pelo Portal Stripe ou falando com a gente." },
                { q: "Vocês fazem migração de dados?", a: "Sim. Para coworkings que já operam, oferecemos migração assistida de planilhas, ERPs e sistemas existentes (planos Professional e Enterprise)." },
                { q: "Posso usar minha própria marca (white-label)?", a: "Sim, no plano Enterprise. Logo, cores, domínio e e-mails customizados." },
                { q: "Quais formas de pagamento aceitam?", a: "Cartão de crédito (mensal recorrente), PIX e boleto via Stripe. Em breve: cobrança via Asaas." },
              ].map((f, i) => (
                <details key={i} className="card-marketing p-5">
                  <summary className="cursor-pointer font-display text-white text-lg flex items-center justify-between">
                    {f.q}
                    <span className="text-[#D4AF37] text-2xl font-light">+</span>
                  </summary>
                  <div className="mt-3 text-white/70 text-sm leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="contato-saas" className="px-6 lg:px-12 py-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-4">Pronto para crescer?</div>
            <h2 className="font-display text-4xl lg:text-5xl text-white font-light leading-tight mb-6">
              Leve o VM Corporate para
              <span className="italic text-[#D4AF37]"> o seu coworking.</span>
            </h2>
            <p className="text-lg text-white/60 mb-10">
              Comece o trial gratuito de 14 dias agora ou agende uma demonstração com a nossa equipe.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/signup" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm" data-testid="saas-cta-signup-bottom">
                Começar trial 14 dias grátis <ArrowRight size={16} />
              </Link>
              <a
                href="https://wa.me/5521975968405?text=Olá!%20Tenho%20interesse%20no%20VM%20Corporate%20SaaS%20para%20meu%20coworking."
                target="_blank" rel="noreferrer"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm"
                data-testid="saas-cta-whatsapp"
              >
                <MessageCircle size={16} /> Falar com vendas
              </a>
            </div>
          </div>
        </section>

        <footer className="border-t border-white/5 px-6 lg:px-12 py-8 text-center text-xs text-white/40">
          © {new Date().getFullYear()} VM Corporate · Software para coworkings premium.
        </footer>
      </div>
    </div>
  );
}
